/* eslint-disable react/prop-types */
import React, {
  useContext,
  useCallback,
  useState,
  useMemo,
  useReducer
} from 'react';
import { SortDirection } from '~/types';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';
import availabilityReducer, {
  updateResourceAvailability
} from '~/modules/resourcing/common/hooks/availabilityReducer';
import { useSessionStorage } from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me';
import { PERIOD_SCALE_ENUM } from '~/modules/resourcing/common/enums';
import useFilterBasedOnSearchCriteria from './useFilterBasedOnSearchCriteria';
import useSetSearchCriteriaEffect from './useSetSearchCriteriaEffect';

export const QuickAllocationContext = React.createContext();

let availabilityTimeout;

const useQuickAllocationContext = () => useContext(QuickAllocationContext);

export const withQuickAllocationContextProvider = BaseComponent => props => {
  const {
    leftComponentWidth,
    chartDates,
    onPrevious,
    onNext,
    scale,
    setScale,
    dateRange,
    defaultDisplayUnit,
    searchCriteria,
    setSearchCriteria,
    page,
    setPage,
    handleExpand,
    userExpandState,
    projectStatusFilterOption,
    setDate
  } = props;

  const {
    featureFlags: {
      isRmpTaskAllocationPhase2Enabled,
      isRmp2025Q4Enhancements,
      isRmp2026Q1Enhancements
    }
  } = useMeContext();

  const {
    storedValue: isShowActualEnabled,
    setValue: setIsShowActualEnabled
  } = useSessionStorage('show-actual-enabled', false);

  const [displayUnit, setDisplayUnit] = useState(defaultDisplayUnit);

  const [state, dispatch] = useReducer(availabilityReducer, {
    availability: false
  });

  const setAvailabilityLoading = useCallback(() => {
    if (availabilityTimeout) {
      clearTimeout(availabilityTimeout);
    }

    dispatch(updateResourceAvailability(true));

    availabilityTimeout = setTimeout(() => {
      dispatch(updateResourceAvailability(false));
    }, 9000);
  }, []);

  const [sort, setSort] = useState({
    field: 'displayText',
    direction: SortDirection.Asc
  });

  useSetSearchCriteriaEffect({ setSearchCriteria });

  const { filter } = useFilterBasedOnSearchCriteria({
    searchCriteria,
    isShowActualEnabled
  });

  const [{ start: chartStartDate }] = chartDates;
  const { end: chartEndDate } = chartDates[chartDates.length - 1];

  const shouldUseScheduleBasedBackgroundImage =
    isRmp2026Q1Enhancements &&
    (scale === PERIOD_SCALE_ENUM.DAYS || scale === PERIOD_SCALE_ENUM.WEEKS);

  const contextValue = useMemo(
    () => ({
      displayUnit,
      setDisplayUnit,
      sort,
      scale,
      periodResolution: mapScaleToPeriodResolution(scale),
      setScale,
      dateRange,
      setAvailabilityLoading,
      state,
      setSort,
      filter,
      searchCriteria,
      leftComponentWidth,
      chartDates,
      chartStartDate,
      chartEndDate,
      resourceUsersCurrentPage: page,
      isShowActualEnabled,
      isRmpTaskAllocationPhase2Enabled,
      setIsShowActualEnabled,
      projectStatusFilterOption,
      setDate,
      isRmp2025Q4Enhancements,
      isRmp2026Q1Enhancements,
      shouldUseScheduleBasedBackgroundImage
    }),
    [
      displayUnit,
      setDisplayUnit,
      filter,
      sort,
      state,
      setAvailabilityLoading,
      setSort,
      projectStatusFilterOption,
      scale,
      setScale,
      dateRange,
      searchCriteria,
      leftComponentWidth,
      chartDates,
      chartStartDate,
      chartEndDate,
      page,
      isShowActualEnabled,
      isRmpTaskAllocationPhase2Enabled,
      setIsShowActualEnabled,
      setDate,
      isRmp2025Q4Enhancements,
      isRmp2026Q1Enhancements,
      shouldUseScheduleBasedBackgroundImage
    ]
  );

  return (
    <QuickAllocationContext.Provider value={contextValue}>
      <BaseComponent
        onPrevious={onPrevious}
        onNext={onNext}
        page={page}
        setPage={setPage}
        handleExpand={handleExpand}
        userExpandState={userExpandState}
      />
    </QuickAllocationContext.Provider>
  );
};

export default useQuickAllocationContext;
